/* Copyright (c) 2017 Backpack Health LLC */

/* global window, document, FileTransfer, cordova, APP_ROOT */

import React from 'react';


import { Grid } from '../components/Grid';
import ContentLoader from '../components/ContentLoader';
import AppNavFile from '../components/appNav/AppNavFile';
import DownloadProgress from '../components/DownloadProgress';
import {
  isMobileOffline,
  alertOfflineMode,
  mobileShareFile,
  headerRequest,
} from '../../utilities/MobileUtil';
import FileModel from '../../models/FileModel';
import { withIntl } from '../../utilities/localizedUtil';

interface Props {
  intl?: any;
  url: string;
  close(): void;
  onDownload?(): void;
  onShare?(): void;
  fileName: string;
  profileId: number;
  noHeader: boolean;
  noCache: boolean;
  children?(downloadProgress: boolean): React.ReactFragment;
  footerHeight?: number;
}

interface State {
  iframeContentLoaded: boolean;
  file: any;
  downloadProgress: boolean;
  downloadDone: boolean;
  downloadError: boolean;
}

@withIntl
class MobilePdfViewer extends React.Component<Props, State> {

  state: State = {
    iframeContentLoaded: false,
    file: null,
    downloadProgress: false,
    downloadDone: false,
    downloadError: false,
  };

  iframeRef: React.ReactFragment;

  constructor(props) {
    super(props);
    this.downloadToLocal = this.downloadToLocal.bind(this);
    this.shareFile = this.shareFile.bind(this);
  }

  componentDidMount() {
    if (this.props.url) {
      this.cachePDF(this.props.url);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.url !== this.props.url) {
      this.setState({
        downloadProgress: false,
        downloadDone: false,
        downloadError: false,
        iframeContentLoaded: false,
        file: null,
      });
    }
  }

  componentDidUpdate() {
    if (this.props.url && !this.state.iframeContentLoaded) {
      this.cachePDF(this.props.url);
    }
  }

  cachePDF(fileURL) {
    const fileTransfer = new FileTransfer();
    // const uri = encodeURI( fileURL); //Reserved for further use
    const name = encodeURIComponent(String(this.props.profileId || this.props.fileName));
    const temp = `${cordova.file.dataDirectory}/${name}.pdf`;
    const fileExist = entry => {
      if (entry && entry.toURL()) {
        const url = `assets/pdfjs/web/viewer.html?file=${encodeURIComponent(entry.toURL())}`;
        this.setState({ file: url, iframeContentLoaded: true });
      }
    };
    const fetchData = cache => {
      fileTransfer.download(
        fileURL,
        temp,
        entry => {
          fileExist(entry);
        },
        error => {
          if (error) {
            fileExist(cache);
          }
        },
        true,
        { encodeURI: false },
      );
    };
    if (this.props.noCache && !isMobileOffline()) {
      resolveLocalFileSystemURL(temp, fetchData, fetchData);
    } else {
      resolveLocalFileSystemURL(temp, fileExist, fetchData);
    }
  }

  downloadToLocal(fileURL) {
    if (this.props.onDownload) this.props.onDownload();

    this.setState({
      downloadProgress: true,
      downloadError: false,
      downloadDone: false,
    });
    if (isMobileOffline()) {
      alertOfflineMode();
      this.setState({
        downloadProgress: false,
        downloadError: true,
        downloadDone: false,
      });
      return;
    }

    const fail = () => {
      this.setState({ downloadProgress: false, downloadError: true });
    };
    const success = () => {
      this.setState({ downloadProgress: false, downloadDone: true });
    };
    headerRequest(fileURL, filename => {
      try {
        cordova.plugins.DownloadManager.download(
          escape(filename),
          fileURL,
          'application/pdf',
          success,
          fail,
        );
      } catch (e) {
        fail();
      }
    });
  }

  shareFile() {
    if (this.props.onShare) this.props.onShare();

    headerRequest(this.props.url, filename => {
      const file = new FileModel({ name: filename, mime_type: 'application/pdf' });
      mobileShareFile(file, this.props.url, this.props.intl, true);
    });
  }

  render() {
    const footerHeight = this.props.footerHeight || 0;
    const height = this.props.noHeader
      ? window.innerHeight - 156 - footerHeight
      : window.innerHeight - footerHeight - 60;
    return (
      <Grid
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <div className="cmp-mobile-pdfpreview">
          {this.props.noHeader ? null : (
            <AppNavFile
              isDisable={this.state.downloadProgress}
              shareFile={this.shareFile}
              downloadFile={this.downloadToLocal}
              doneWithFile={this.props.close}
              fileDownloadUrl={this.props.url}
            />
          )}
          <div className="pdf-content">
            {this.state.file ? (
              <ContentLoader
                isContentLoaded={this.state.iframeContentLoaded}
                color="white"
              >
                <iframe
                  ref={i => {
                    this.iframeRef = i;
                  }}
                  src={this.state.file}
                  style={{
                    width: '100%',
                    height: `${height}px`,
                    margin: '0 auto',
                  }}
                />

              </ContentLoader>
            ) : null}

            {
              this.props.children && this.props.children(this.state.downloadProgress)
            }
          </div>
          <DownloadProgress
            isVisible={true}
            isDownloading={this.state.downloadProgress}
            isDone={this.state.downloadDone}
            isError={this.state.downloadError}
            styles={{ bottom: 60 }}
          />
        </div>
      </Grid>
    );
  }
}

export default MobilePdfViewer;
